import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormGroup, FormsModule } from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  Subject,
  switchMap,
} from "rxjs";
import { ProgramClientListDto } from "../../../../core/models/program/programClientListDto";
import { ProgramListDto } from "../../../../core/models/program/programListDto";
import { ProgramsService } from "../../../../core/services/programs.service";
import { GetD365DefaultProgramsAndSalesClassificationByClientResponse } from "../../../../core/models/program/getD365DefaultProgramsAndSalesClassificationByClientResponse";
import { CommonModule } from "@angular/common";
import { TableModule } from "primeng/table";
import { Popover, PopoverModule } from "primeng/popover";
import { InputTextModule } from "primeng/inputtext";
import { GenericResponseDto } from "../../../../core/models/generic-response.dto";

@UntilDestroy()
@Component({
  selector: "jni-program",
  templateUrl: "./program.component.html",
  imports: [
    CommonModule,
    TableModule,
    FormsModule,
    PopoverModule,
    InputTextModule,
  ],
  styleUrls: ["./program.component.scss"],
})
export class ProgramComponent implements OnInit {
  public program$ = new Subject<string>();
  // public customerVendor$ = new Subject<string>();
  programList: ProgramListDto[] = [];
  showSmallLoader = false;
  programId = 0;

  programData = {} as ProgramListDto;
  salesOrderProgramData = {} as ProgramClientListDto;
  salesOrderProgramList: ProgramClientListDto[] = [];
  isProgramNameValid = false;
  isDisabled = false;
  getD365DefaultProgramsAndSalesClassification =
    {} as GetD365DefaultProgramsAndSalesClassificationByClientResponse;

  @Input() isSalesOrder = false;
  @Input() customerAccount = "";
  @Input() parent!: FormGroup;
  @Input() programName = "";
  @Input() formErrors: Record<string, string> = {};
  @Output() smallLoaderEvent = new EventEmitter<boolean>();
  @Output() programEvent = new EventEmitter<
    ProgramListDto | ProgramClientListDto
  >();
  @Output() disableBuyer = new EventEmitter<boolean>();

  constructor(private _programService: ProgramsService) {}

  ngOnInit() {
    this.searchProgramrName();
  }

  onProgramSelection(
    event: ProgramClientListDto | ProgramListDto,
    clientProspetToggle: Popover | null
  ) {
    if (event) {
      if (!this.isSalesOrder) {
        this.programId = event.id ?? 0;
        this.programName = event.name ?? "";
        if ("office" in event) {
          this.programData = event as ProgramListDto;
        }
        if (clientProspetToggle) {
          clientProspetToggle.hide();
        }
        this.programEvent.emit(event);
      } else {
        if ("programId" in event) {
          this.programId = event.programId;
        }
        if ("programName" in event) {
          this.programName = event.programName;
        }
        if ("clientAccount" in event) {
          this.salesOrderProgramData = event;
        }
        if (clientProspetToggle) {
          clientProspetToggle.hide();
        }
        this.disableBuyer.emit(true);
        this.programEvent.emit(event);
      }
    }
  }

  onProgramSearchOpenToggle(overlayPanel: Popover, event: Event) {
    overlayPanel.toggle(event);
  }

  programFieldFocusOut(overlayPanel: Popover) {
    overlayPanel.hide();
  }

  // patchValueInCustomerAccountField(programObj: ProgramListDto) {
  //   this.programId= programObj.id;
  //   this.programName = programObj.name;
  // }

  searchProgramrName() {
    if (!this.isSalesOrder) {
      this.program$
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          switchMap((searchitem: string) => {
            if (searchitem) {
              this.smallLoaderEvent.emit(true);
            }
            const programName = searchitem;
            this.programList = [];
            if (programName != "" && programName != undefined) {
              return this._programService.getProgramByNameFilter(programName);
            }
            return EMPTY;
          })
        )
        .subscribe({
          next: (res: GenericResponseDto<ProgramListDto[]>) => {
            if (res.data != null) {
              this.programList = res.data;
            }
            this.smallLoaderEvent.emit(false);
          },
          error: () => {
            this.smallLoaderEvent.emit(false);
          },
        });
    } else {
      this.disableBuyer.emit(true);
      this.program$
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          switchMap((searchitem: string) => {
            if (searchitem) {
              this.smallLoaderEvent.emit(true);
            }
            this.disableBuyer.emit(false);
            const programName = searchitem;
            this.programList = [];
            if (programName != "" && programName != undefined) {
              return this._programService.getProgramByNameFilterForSalesOrder(
                programName,
                this.customerAccount ?? ""
              );
            }
            return EMPTY;
          })
        )
        .subscribe({
          next: (res: GenericResponseDto<ProgramClientListDto[]>) => {
            if (res.data != null) {
              this.salesOrderProgramList = res.data;
            }
            this.smallLoaderEvent.emit(false);
          },
          error: () => {
            this.smallLoaderEvent.emit(false);
          },
        });
    }
  }

  logValidationErrors() {
    if (!this.programName || this.programName === "") {
      this.isProgramNameValid = true;
    } else {
      this.isProgramNameValid = false;
    }
  }

  getProgramDetails(event: Event, overlayPanel: Popover) {
    const input = event.target as HTMLInputElement;
    const keyboardEvent = event as KeyboardEvent;

    if (keyboardEvent.key.toLowerCase() === "tab") {
      const programValue = input.value;
      this.smallLoaderEvent.emit(true);

      if (!this.isSalesOrder) {
        this._programService
          .getProgramByNameFilter(programValue)
          .pipe(untilDestroyed(this))
          .subscribe({
            next: res => {
              this.programList = res.data;
              if (this.programList.length > 0) {
                const defaultProgram = this.programList.find(
                  x => x.defaultClient === true
                );
                this.onProgramSelection(
                  defaultProgram ?? this.programList[0],
                  overlayPanel
                );
              }
              this.showSmallLoader = false;
              this.smallLoaderEvent.emit(false);
            },
            error: () => {
              this.showSmallLoader = false;
              this.smallLoaderEvent.emit(false);
            },
          });
      } else {
        this._programService
          .getProgramByNameFilterForSalesOrder(
            programValue,
            this.customerAccount ?? ""
          )
          .pipe(untilDestroyed(this))
          .subscribe({
            next: res => {
              this.salesOrderProgramList = res.data;
              if (this.salesOrderProgramList.length > 0) {
                this.onProgramSelection(
                  this.salesOrderProgramList[0],
                  overlayPanel
                );
              }
              this.showSmallLoader = false;
              this.smallLoaderEvent.emit(false);
            },
            error: () => {
              this.showSmallLoader = false;
              this.smallLoaderEvent.emit(false);
            },
          });
      }
    }
  }

  disableEnableProgram(status: boolean) {
    this.isDisabled = status;
  }

  getAllProgramDetailsByCustomerAccount(
    customerAccount: string,
    createOrderCase: boolean
  ) {
    if (customerAccount) {
      this.customerAccount = customerAccount;

      // Clear the provious program details each time the customer account is changed
      if (createOrderCase) {
        this.programName = "";
        this.programId = 0;
      }

      const obj: ProgramClientListDto = {} as ProgramClientListDto;
      obj.clientAccount = customerAccount;
      obj.programId = this.programId;
      this.programEvent.emit(obj);
      this._programService
        .getProgramByNameFilterForSalesOrder("", customerAccount)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            this.salesOrderProgramList = res.data;
            this.showSmallLoader = false;
            if (this.salesOrderProgramList.length > 0) {
              const defaultProgramData = this.salesOrderProgramList.find(
                x => x.defaultClient === true
              );
              if (defaultProgramData) {
                this.onProgramSelection(defaultProgramData, null);
              } else {
                this.getD365DefaultProgramsAndSalesClassificationByClient(
                  customerAccount
                );
              }
            }
          },
          error: () => {
            this.showSmallLoader = false;
          },
        });
    }
  }

  getD365DefaultProgramsAndSalesClassificationByClient(
    customerAccount: string
  ) {
    this._programService
      .getD365DefaultProgramsAndSalesClassificationByClient(customerAccount)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          if (res?.status === "Success" && res?.data) {
            this.getD365DefaultProgramsAndSalesClassification = res.data;
            if (
              this.getD365DefaultProgramsAndSalesClassification
                ?.d365DefaultPrgramCode &&
              this.getD365DefaultProgramsAndSalesClassification
                ?.d365DefaultPrgram
            ) {
              const sortedList = this.sortDatesDescending(
                this.salesOrderProgramList
              );
              const defaultProgramIdReadonly = sortedList.find(
                x =>
                  x.dimensionD365 ===
                  this.getD365DefaultProgramsAndSalesClassification
                    .d365DefaultPrgramCode
              );
              if (defaultProgramIdReadonly) {
                this.onProgramSelection(defaultProgramIdReadonly, null);
                this.disableEnableProgram(true);
              }
            }
          }
        },
      });
  }

  sortDatesDescending(list: ProgramClientListDto[]): ProgramClientListDto[] {
    list = list.sort((a, b) => {
      const dateA = new Date(a.programCreatedDate);
      const dateB = new Date(b.programCreatedDate);
      return dateB.getTime() - dateA.getTime();
    });

    return list;
  }
}
